import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { SortKey } from '../../types'

interface Column {
  /** SortKey field, or null for non-sortable columns. */
  field: SortKey['field'] | null
  label: string
  /** Right-align header text (numeric columns). */
  rightAlign?: boolean
  /** Hidden visually but kept in DOM for screen readers (icon-only columns). */
  visuallyHidden?: boolean
}

const COLUMNS: readonly Column[] = [
  { field: null, label: 'Select', visuallyHidden: true },
  // Header label intentionally short ("Prio") — the column carries a
  // dot + 3-letter band (HIG/MED/LOW/CRT) so the full word is
  // redundant and was eating ~24px the Scenario column needed.
  { field: 'priority', label: 'Prio' },
  { field: 'alert_number', label: 'Alert' },
  { field: 'scenario_name', label: 'Scenario' },
  { field: 'score', label: 'Score', rightAlign: true },
  { field: null, label: 'Customer' },
  { field: 'amount_minor', label: 'Amount', rightAlign: true },
  { field: 'channel', label: 'Channel' },
  // Status stays icon-only — the column width (24px) is intentionally
  // sized for the icon glyph; rendering "STATUS" would overflow.
  { field: 'status', label: 'Status', visuallyHidden: true },
  { field: 'sla_remaining', label: 'SLA' },
  { field: null, label: 'Assigned' },
  { field: 'created_at', label: 'Created' },
] as const

interface Props {
  sort: SortKey[]
  onSort: (field: SortKey['field'], multi: boolean) => void
}

export function AlertsTableHeader({ sort, onSort }: Props) {
  return (
    <div
      role="row"
      className="sticky top-0 z-10 grid h-9 items-center gap-3 border-b border-border bg-card/95 px-3 text-xs font-medium uppercase tracking-wide text-muted-fg backdrop-blur grid-cols-[40px_48px_96px_minmax(20rem,1.4fr)_56px_minmax(10rem,1fr)_128px_56px_24px_112px_80px_80px]"
    >
      {COLUMNS.map((col, idx) => {
        const sortIndex = col.field ? sort.findIndex((s) => s.field === col.field) : -1
        const dir = sortIndex >= 0 ? sort[sortIndex]!.dir : null
        const sortable = col.field !== null
        const content = sortable ? (
          <button
            type="button"
            onClick={(e) => onSort(col.field!, e.shiftKey)}
            className={cn(
              'flex items-center gap-1 hover:text-fg focus-visible:outline-none focus-visible:text-fg',
              col.rightAlign && 'ml-auto',
            )}
          >
            <span className={cn(col.visuallyHidden && 'sr-only')}>{col.label}</span>
            {dir === 'asc' && <ArrowUp size={10} />}
            {dir === 'desc' && <ArrowDown size={10} />}
            {dir === null && !col.visuallyHidden && (
              <ChevronsUpDown size={10} className="opacity-30" />
            )}
            {sortIndex >= 0 && sort.length > 1 && (
              <span className="text-[0.5rem] font-mono">{sortIndex + 1}</span>
            )}
          </button>
        ) : (
          <span
            className={cn(col.visuallyHidden && 'sr-only', col.rightAlign && 'ml-auto')}
          >
            {col.label}
          </span>
        )
        return (
          // role="columnheader" — every direct child of role="row"
          // inside a grid must be a cell / rowheader / columnheader,
          // per WAI-ARIA. axe flags bare divs as the
          // aria-required-children violation.
          <div key={`${col.label}-${idx}`} role="columnheader">
            {content}
          </div>
        )
      })}
    </div>
  )
}
